import { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  type Query,
  type DocumentData,
} from "firebase/firestore";
import { getDb, FIRESTORE_PATH } from "../lib/firebase";
import { useOnlineStatus } from "./useOnlineStatus";

interface Options {
  orderField?: string;
  orderDir?: "asc" | "desc";
}

export function useFirestoreCollection<T extends { id: string }>(
  subPath: string,
  opts: Options = {},
): { data: T[]; ready: boolean; offline: boolean; isLoading: boolean } {
  const cached = loadLocal<T>(subPath);
  const [data, setData] = useState<T[]>(cached);
  const [ready, setReady] = useState(false);
  const online = useOnlineStatus();

  useEffect(() => {
    const db = getDb();
    if (!db) {
      setReady(true);
      return;
    }
    const colRef = collection(db, `${FIRESTORE_PATH}/${subPath}`);
    const q: Query<DocumentData> = opts.orderField
      ? query(colRef, orderBy(opts.orderField, opts.orderDir ?? "asc"))
      : colRef;
    const unsub = onSnapshot(
      q,
      (snap) => {
        const arr = snap.docs.map(
          (d) => ({ id: d.id, ...d.data() }) as unknown as T,
        );
        setData(arr);
        saveLocal(subPath, arr);
        setReady(true);
      },
      () => setReady(true),
    );
    return unsub;
  }, [subPath, opts.orderField, opts.orderDir]);

  // Show loading only on first ever load (no cache + not yet ready + online)
  const isLoading = !ready && cached.length === 0 && !!getDb() && online;

  // True offline: real network state OR Firebase not configured.
  // The Firestore SDK queues writes via IndexedDB persistence and replays
  // them automatically on reconnect.
  const offline = !online || !getDb();

  return { data, ready, offline, isLoading };
}

function localKey(p: string) {
  return `bp26.cache.${p}`;
}
function loadLocal<T>(p: string): T[] {
  try {
    const v = localStorage.getItem(localKey(p));
    return v ? (JSON.parse(v) as T[]) : [];
  } catch {
    return [];
  }
}
function saveLocal<T>(p: string, data: T[]): void {
  try {
    localStorage.setItem(localKey(p), JSON.stringify(data));
  } catch {
    /* quota — ignore */
  }
}
